import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ZoomIn, ZoomOut, Move, Info, X, MapPin } from 'lucide-react';
import EquipmentDetailSummary from '../../components/EquipmentDetailSummary';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Select from '../../components/Select';
import Modal from '../../components/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { loadPlanImageElement } from '../../utils/planImage';
import { EQUIPMENT_COLORS, EQUIPMENT_ICONS, EQUIPMENT_TYPE_LABELS } from '../../utils/constants';
import { formatDate, renderCell } from '../../utils/helpers';

const planIsDefault = (plan) => plan?.isDefault === 1 || plan?.isDefault === true;

const InteractivePlan = () => {
  const { user } = useAuth();
  const { error: toastError } = useToast();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const allPlacementsRef = useRef([]);
  const equipmentLookupRef = useRef({});
  const planBlobUrlRef = useRef(null);
  const planImageLoadSeqRef = useRef(0);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planImage, setPlanImage] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [hoveredEquipment, setHoveredEquipment] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    if (user?.id) loadData();
  }, [user?.id]);

  useEffect(() => {
    return () => {
      if (planBlobUrlRef.current) {
        URL.revokeObjectURL(planBlobUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (planImage) {
      drawCanvas();
    }
  }, [planImage, equipment, zoom, pan, selectedEquipment, hoveredEquipment]);

  const mergePlacementsWithEquipment = (placementsData) =>
    placementsData.map((placement) => {
      const details = equipmentLookupRef.current[placement.equipmentId] || {};
      return {
        ...details,
        ...placement,
        id: placement.equipmentId || placement.id,
        serialNumber: placement.serialNumber || details.serialNumber,
        type: placement.type || details.type,
        positionX: placement.positionX,
        positionY: placement.positionY,
      };
    });

  const loadPlacementsForPlan = async (planId) => {
    const placementsData = await apiService.getPlacements(planId);
    const merged = mergePlacementsWithEquipment(placementsData);
    allPlacementsRef.current = merged;
    setEquipment(merged);
  };

  const loadData = async () => {
    setLoading(true);
    let plansData = [];

    try {
      try {
        plansData = await apiService.getMyPlans();
      } catch (myPlansError) {
        if (user?.id == null) throw myPlansError;
        plansData = await apiService.getClientPlans(user.id);
      }
      setPlans(plansData);
    } catch (error) {
      console.error('Failed to load plans:', error);
      toastError(error.message || 'Impossible de charger vos plans');
      setPlans([]);
    }

    try {
      const equipmentData = await apiService.getEquipment(user?.id);
      equipmentLookupRef.current = Object.fromEntries(
        equipmentData.map((item) => [item.id, item])
      );
    } catch (error) {
      console.error('Failed to load equipment for plan legend:', error);
      equipmentLookupRef.current = {};
    }

    try {
      const defaultPlan = plansData.find((p) => planIsDefault(p)) || plansData[0];
      if (defaultPlan) {
        setSelectedPlan(defaultPlan);
        await loadPlacementsForPlan(defaultPlan.id);
        loadPlanImage(defaultPlan);
      } else {
        allPlacementsRef.current = [];
        setEquipment([]);
        setSelectedPlan(null);
        setPlanImage(null);
      }
    } catch (error) {
      console.error('Failed to load plan details:', error);
      toastError(error.message || 'Impossible de charger le détail du plan');
    } finally {
      setLoading(false);
    }
  };

  const loadPlanImage = async (plan) => {
    if (!apiService.planHasFile(plan)) {
      setPlanImage(null);
      return;
    }

    const loadSeq = ++planImageLoadSeqRef.current;

    try {
      if (planBlobUrlRef.current) {
        URL.revokeObjectURL(planBlobUrlRef.current);
        planBlobUrlRef.current = null;
      }

      const { img, blobUrl } = await loadPlanImageElement(plan);
      if (loadSeq !== planImageLoadSeqRef.current) {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        return;
      }
      planBlobUrlRef.current = blobUrl;
      setPlanImage(img);
    } catch (error) {
      if (loadSeq !== planImageLoadSeqRef.current) return;
      console.error('Failed to load plan image:', error);
      setPlanImage(null);
      toastError(error.message || 'Impossible de charger l\'image du plan');
    }
  };

  const handlePlanChange = async (e) => {
    const planId = parseInt(e.target.value, 10);
    const plan = plans.find((p) => Number(p.id) === Number(planId));
    if (plan) {
      setSelectedPlan(plan);
      setPlanImage(null);
      setSelectedEquipment(null);
      setShowDetailsModal(false);
      try {
        await loadPlacementsForPlan(plan.id);
      } catch (error) {
        console.error('Failed to load placements:', error);
        toastError(error.message || 'Impossible de charger les emplacements');
      }
      loadPlanImage(plan);
    }
  };

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;
    const x = (clickX - pan.x) / zoom;
    const y = (clickY - pan.y) / zoom;
    const drawWidth = canvas.width;
    const drawHeight = (drawWidth * planImage.height) / planImage.width;
    return { x, y, drawWidth, drawHeight };
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !planImage) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);
    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);

    const aspectRatio = planImage.width / planImage.height;
    const drawWidth = width;
    const drawHeight = drawWidth / aspectRatio;
    ctx.drawImage(planImage, 0, 0, drawWidth, drawHeight);

    equipment.forEach((item) => {
      const x = item.positionX * drawWidth;
      const y = item.positionY * drawHeight;
      const isSelected = selectedEquipment?.id === item.id;
      const isHovered = hoveredEquipment?.id === item.id;

      // Draw glow effect for selected or hovered
      if (isSelected || isHovered) {
        ctx.beginPath();
        ctx.arc(x, y, 20, 0, 2 * Math.PI);
        ctx.fillStyle = isSelected ? 'rgba(255, 0, 0, 0.2)' : 'rgba(255, 0, 0, 0.1)';
        ctx.fill();
      }

      // Draw marker
      ctx.beginPath();
      ctx.arc(x, y, isSelected ? 12 : isHovered ? 10 : 8, 0, 2 * Math.PI);
      ctx.fillStyle = EQUIPMENT_COLORS[item.type] || '#ff0000';
      ctx.fill();
      ctx.strokeStyle = isSelected ? '#000' : isHovered ? '#333' : '#fff';
      ctx.lineWidth = isSelected ? 3 : isHovered ? 2.5 : 2;
      ctx.stroke();

      // Draw label for selected or hovered
      if (isSelected || isHovered) {
        ctx.fillStyle = '#000';
        ctx.font = isSelected ? 'bold 14px Arial' : 'bold 12px Arial';
        const text = item.serialNumber;
        const textWidth = ctx.measureText(text).width;
        
        // Background for text
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.fillRect(x + 15, y - 15, textWidth + 10, 25);
        
        ctx.fillStyle = '#000';
        ctx.fillText(text, x + 20, y + 5);
      }
    });

    ctx.restore();
  };

  const handleCanvasClick = (e) => {
    if (!planImage || isPanning) return;

    const { x, y, drawWidth, drawHeight } = getCanvasCoordinates(e);

    let clickedEquipment = null;
    equipment.forEach((item) => {
      const equipX = item.positionX * drawWidth;
      const equipY = item.positionY * drawHeight;
      const distance = Math.sqrt(Math.pow(x - equipX, 2) + Math.pow(y - equipY, 2));
      if (distance <= 15) {
        clickedEquipment = item;
      }
    });

    if (clickedEquipment) {
      setSelectedEquipment(clickedEquipment);
      setShowDetailsModal(true);
    } else {
      setSelectedEquipment(null);
      setShowDetailsModal(false);
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!planImage) return;

    const canvas = canvasRef.current;

    if (isPanning) {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const deltaX = (e.clientX - panStart.x) * scaleX;
      const deltaY = (e.clientY - panStart.y) * scaleY;
      setPan({ x: deltaX, y: deltaY });
      return;
    }

    const { x, y, drawWidth, drawHeight } = getCanvasCoordinates(e);

    let hoveredItem = null;
    equipment.forEach((item) => {
      const equipX = item.positionX * drawWidth;
      const equipY = item.positionY * drawHeight;
      const distance = Math.sqrt(Math.pow(x - equipX, 2) + Math.pow(y - equipY, 2));
      if (distance <= 15) {
        hoveredItem = item;
      }
    });

    setHoveredEquipment(hoveredItem);
    canvas.style.cursor = hoveredItem ? 'pointer' : isPanning ? 'grabbing' : 'grab';
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0 || !planImage) return;
    const { x, y, drawWidth, drawHeight } = getCanvasCoordinates(e);
    const canvas = canvasRef.current;

    // Check if clicking on equipment
    let clickedEquipment = false;
    equipment.forEach((item) => {
      const equipX = item.positionX * drawWidth;
      const equipY = item.positionY * drawHeight;
      const distance = Math.sqrt(Math.pow(x - equipX, 2) + Math.pow(y - equipY, 2));
      if (distance <= 15) {
        clickedEquipment = true;
      }
    });

    if (!clickedEquipment) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      canvas.style.cursor = 'grabbing';
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = hoveredEquipment ? 'pointer' : 'grab';
    }
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.5));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mon Plan de Masse</h1>
          <p className="text-gray-600 mt-1">Visualisez vos équipements sur le plan</p>
        </div>
        <Card>
          <div className="text-center py-12">
            <Info className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun plan enregistré</h3>
            <p className="text-gray-600">
              Aucun plan n&apos;est associé à votre compte. Contactez l&apos;administrateur.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Mon Plan de Masse</h1>
        <p className="text-gray-600 mt-1">
          Visualisez l'emplacement de vos équipements de sécurité
        </p>
      </div>

      {selectedPlan && (
        <Card className="border-l-4 border-red-500">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                {selectedPlan.name}
                {planIsDefault(selectedPlan) && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                    Plan principal
                  </span>
                )}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {[selectedPlan.location, selectedPlan.floor].filter(Boolean).join(' · ') ||
                  'Emplacement non renseigné'}
              </p>
              {selectedPlan.uploadedAt && (
                <p className="text-xs text-gray-500 mt-1">
                  Mis à jour : {formatDate(new Date(selectedPlan.uploadedAt), 'dd/MM/yyyy')}
                </p>
              )}
            </div>
            <p className="text-sm text-gray-600">
              {apiService.planHasFile(selectedPlan)
                ? planImage
                  ? 'Plan affiché ci-dessous'
                  : 'Fichier du plan en cours de chargement…'
                : 'Plan enregistré sans fichier image/PDF'}
            </p>
          </div>
        </Card>
      )}

      {plans.length > 1 && (
        <Card>
          <div className="flex items-center space-x-4">
            <MapPin className="w-5 h-5 text-red-600 shrink-0" />
            <div className="flex-1">
              <Select
                label="Sélectionner un plan"
                name="planId"
                value={selectedPlan?.id ?? ''}
                onChange={handlePlanChange}
                placeholder="Choisir un plan..."
                options={plans.map((plan) => ({
                  value: plan.id,
                  label: `${plan.name}${plan.location ? ` - ${plan.location}` : ''}${plan.floor ? ` (${plan.floor})` : ''}${planIsDefault(plan) ? ' ⭐' : ''}`,
                }))}
              />
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Equipment List */}
        <Card className="lg:col-span-1">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Légende ({equipment.length})
          </h2>
          <div className="space-y-2">
            {equipment.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedEquipment(item)}
                className={`w-full p-3 rounded-lg text-left transition-all ${
                  selectedEquipment?.id === item.id
                    ? 'bg-primary-50 border-2 border-primary-500'
                    : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: EQUIPMENT_COLORS[item.type] }}
                  />
                  <span className="text-xl">{EQUIPMENT_ICONS[item.type]}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.serialNumber}
                    </p>
                    <p className="text-xs text-gray-500">{EQUIPMENT_TYPE_LABELS[item.type] || item.type}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-3">
          <div className="space-y-4">
            {planImage ? (
              <>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    {selectedEquipment ? (
                      <div>
                        <p className="font-medium text-gray-900">{renderCell(selectedEquipment.serialNumber)}</p>
                        <p className="text-xs">{selectedEquipment.location || 'Emplacement non spécifié'}</p>
                      </div>
                    ) : (
                      <p>Cliquez sur un équipement pour voir les détails</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm" onClick={handleZoomOut}>
                      <ZoomOut className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600">{Math.round(zoom * 100)}%</span>
                    <Button variant="outline" size="sm" onClick={handleZoomIn}>
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                  <canvas
                    ref={canvasRef}
                    width={800}
                    height={600}
                    onClick={handleCanvasClick}
                    onMouseMove={handleCanvasMouseMove}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={() => {
                      setIsPanning(false);
                      setHoveredEquipment(null);
                    }}
                    className="cursor-grab w-full"
                    style={{ touchAction: 'none' }}
                  />
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Navigation :</strong> Cliquez et glissez pour déplacer le plan. Survolez les
                    équipements pour les voir en surbrillance. Cliquez pour ouvrir la fiche.
                  </p>
                </div>
              </>
            ) : (
              <div className="text-center py-12 px-4 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                <Info className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-base font-semibold text-gray-900 mb-2">Plan non affiché</h3>
                <p className="text-sm text-gray-600 max-w-md mx-auto">
                  {selectedPlan && !apiService.planHasFile(selectedPlan)
                    ? 'Ce plan existe dans votre dossier mais aucun fichier image ou PDF n\'y est joint. Demandez à l\'administrateur de téléverser le plan.'
                    : 'Le fichier du plan n\'a pas pu être chargé. Utilisez la légende à gauche pour consulter les équipements positionnés.'}
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Equipment Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedEquipment(null);
        }}
        title="Détails de l'équipement"
      >
        {selectedEquipment && (
          <div className="space-y-6 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center space-x-4 pb-4 border-b">
              <div className="text-5xl">{EQUIPMENT_ICONS[selectedEquipment.type]}</div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900">
                  {selectedEquipment.serialNumber}
                </h3>
                <p className="text-gray-600 text-lg">{EQUIPMENT_TYPE_LABELS[selectedEquipment.type] || selectedEquipment.type}</p>
              </div>
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: EQUIPMENT_COLORS[selectedEquipment.type] }}
              />
            </div>

            <EquipmentDetailSummary
              equipment={selectedEquipment}
              columns={2}
              dense
              excludeKeys={['clientName', 'clientCompany']}
            />

            {/* Actions */}
            <div className="flex space-x-3 pt-4 border-t">
              <Button
                variant="primary"
                className="flex-1"
                onClick={() => navigate(`/my-equipment/${selectedEquipment.id}`)}
              >
                Voir tous les détails
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedEquipment(null);
                }}
              >
                Fermer
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InteractivePlan;

